import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Camera, MapPin, Clock, Users, 
  AlertTriangle, Maximize2, Minimize2,
  Activity, Eye, Wifi, WifiOff
} from 'lucide-react';

interface CameraFeed {
  id: string;
  name: string;
  location: string;
  status: 'online' | 'offline' | 'maintenance';
  lastUpdate: string;
  peopleDetected: number;
  aiAnalysis: string;
  thumbnail: string;
  zone: string;
}

const PublicCameraFeed: React.FC = () => {
  const [cameras, setCameras] = useState<CameraFeed[]>([
    {
      id: 'cam1',
      name: 'Camera #A-234',
      location: 'Zone A - Main Street',
      status: 'online',
      lastUpdate: 'Just now',
      peopleDetected: 47,
      aiAnalysis: 'Critical: Flooding detected, multiple people on rooftops',
      thumbnail: '🌊',
      zone: 'Zone A'
    },
    {
      id: 'cam2',
      name: 'Camera #B-123',
      location: 'Zone B - Market Area',
      status: 'online',
      lastUpdate: '2 min ago',
      peopleDetected: 12,
      aiAnalysis: 'Warning: Fire detected, evacuation in progress',
      thumbnail: '🔥',
      zone: 'Zone B'
    },
    {
      id: 'cam3',
      name: 'Camera #C-456',
      location: 'Zone C - Residential',
      status: 'online',
      lastUpdate: '5 min ago',
      peopleDetected: 3,
      aiAnalysis: 'Monitor: Structural damage, people moving',
      thumbnail: '🏢',
      zone: 'Zone C'
    },
    {
      id: 'cam4',
      name: 'Camera #A-789',
      location: 'Zone A - River Bridge',
      status: 'offline',
      lastUpdate: '15 min ago',
      peopleDetected: 0,
      aiAnalysis: 'Offline - Maintenance required',
      thumbnail: '📹',
      zone: 'Zone A'
    }
  ]);

  const [fullscreenCam, setFullscreenCam] = useState<string | null>(null);
  const [filterZone, setFilterZone] = useState<string>('all');

  const getStatusColor = (status: string) => {
    switch(status) {
      case 'online': return 'text-green-500 border-green-500 bg-green-500/10';
      case 'offline': return 'text-red-500 border-red-500 bg-red-500/10';
      default: return 'text-yellow-500 border-yellow-500 bg-yellow-500/10';
    }
  };

  const getZoneColor = (zone: string) => {
    switch(zone) {
      case 'Zone A': return 'border-red-500/30 bg-red-500/5';
      case 'Zone B': return 'border-orange-500/30 bg-orange-500/5';
      case 'Zone C': return 'border-cyan-500/30 bg-cyan-500/5';
      default: return 'border-gray-500/30';
    }
  };

  const filteredCameras = cameras.filter(cam => 
    filterZone === 'all' || cam.zone === filterZone
  );

  return (
    <div className="bg-gray-900/50 backdrop-blur-xl border border-cyan-500/30 rounded-xl p-6 holographic-card">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Camera className="text-green-500 animate-pulse" />
            PUBLIC CAMERA INTEGRATION
          </h2>
          <p className="text-xs text-gray-400 mt-1">AI-powered monitoring of 847 cameras</p>
        </div>

        {/* Zone Filter */}
        <select
          value={filterZone}
          onChange={(e) => setFilterZone(e.target.value)}
          className="bg-black/50 border border-cyan-500/30 rounded-lg px-3 py-2 text-sm"
        >
          <option value="all">All Zones</option>
          <option value="Zone A">Zone A</option>
          <option value="Zone B">Zone B</option>
          <option value="Zone C">Zone C</option>
        </select>
      </div>

      {/* Camera Grid */}
      <div className="grid grid-cols-2 gap-4">
        {filteredCameras.map((camera) => (
          <motion.div
            key={camera.id}
            layout
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`border rounded-xl overflow-hidden ${getZoneColor(camera.zone)}`}
          >
            {/* Camera Preview */}
            <div className="relative aspect-video bg-black/50 flex items-center justify-center">
              <span className="text-6xl">{camera.thumbnail}</span>
              
              {/* Status Badge */}
              <div className={`absolute top-2 left-2 flex items-center gap-1 px-2 py-1 rounded-lg text-xs ${getStatusColor(camera.status)}`}>
                {camera.status === 'online' ? <Wifi size={12} /> : <WifiOff size={12} />}
                {camera.status.toUpperCase()}
              </div>

              {/* People Count */}
              <div className="absolute top-2 right-2 bg-black/70 px-2 py-1 rounded-lg text-xs flex items-center gap-1">
                <Users size={12} className="text-cyan-500" />
                <span>{camera.peopleDetected}</span>
              </div>

              {/* Expand Button */}
              <button
                onClick={() => setFullscreenCam(fullscreenCam === camera.id ? null : camera.id)}
                className="absolute bottom-2 right-2 bg-black/70 hover:bg-black p-1 rounded"
              >
                {fullscreenCam === camera.id ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
              </button>
            </div>

            {/* Camera Info */}
            <div className="p-3">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h3 className="font-bold text-sm">{camera.name}</h3>
                  <p className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                    <MapPin size={10} />
                    {camera.location}
                  </p>
                </div>
                <span className="text-xs text-gray-400 flex items-center gap-1">
                  <Clock size={10} />
                  {camera.lastUpdate}
                </span>
              </div>

              {/* AI Analysis */}
              <div className="mt-2 p-2 bg-black/30 rounded-lg border border-cyan-500/20">
                <div className="flex items-center gap-1 mb-1">
                  <Activity size={12} className="text-cyan-500" />
                  <span className="text-xs font-bold">AI ANALYSIS</span>
                </div>
                <p className="text-xs text-gray-300">{camera.aiAnalysis}</p>
              </div>

              {/* Actions */}
              <div className="flex gap-2 mt-3">
                <button className="flex-1 bg-cyan-600/20 hover:bg-cyan-600/30 text-cyan-500 text-xs font-bold py-2 px-3 rounded-lg border border-cyan-500/30">
                  View Feed
                </button>
                <button className="flex-1 bg-purple-600/20 hover:bg-purple-600/30 text-purple-500 text-xs font-bold py-2 px-3 rounded-lg border border-purple-500/30">
                  Analyze
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Fullscreen View Modal */}
      <AnimatePresence>
        {fullscreenCam && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 flex items-center justify-center z-50"
            onClick={() => setFullscreenCam(null)}
          >
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
              className="w-[800px] bg-gray-900 rounded-xl p-6 border border-cyan-500/30"
              onClick={(e) => e.stopPropagation()}
            >
              {cameras.find(c => c.id === fullscreenCam) && (
                <>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold">
                      {cameras.find(c => c.id === fullscreenCam)?.name}
                    </h3>
                    <button onClick={() => setFullscreenCam(null)}>✕</button>
                  </div>
                  <div className="aspect-video bg-black/50 rounded-lg flex items-center justify-center">
                    <span className="text-8xl">
                      {cameras.find(c => c.id === fullscreenCam)?.thumbnail}
                    </span>
                  </div>
                  <div className="mt-4 grid grid-cols-3 gap-4">
                    <div className="p-3 bg-black/30 rounded-lg">
                      <p className="text-xs text-gray-400">Location</p>
                      <p className="text-sm font-bold">
                        {cameras.find(c => c.id === fullscreenCam)?.location}
                      </p>
                    </div>
                    <div className="p-3 bg-black/30 rounded-lg">
                      <p className="text-xs text-gray-400">People Detected</p>
                      <p className="text-sm font-bold text-cyan-500">
                        {cameras.find(c => c.id === fullscreenCam)?.peopleDetected}
                      </p>
                    </div>
                    <div className="p-3 bg-black/30 rounded-lg">
                      <p className="text-xs text-gray-400">Status</p>
                      <p className={`text-sm font-bold ${
                        cameras.find(c => c.id === fullscreenCam)?.status === 'online' 
                          ? 'text-green-500' : 'text-red-500'
                      }`}>
                        {cameras.find(c => c.id === fullscreenCam)?.status?.toUpperCase()}
                      </p>
                    </div>
                  </div>
                  <button className="w-full mt-4 bg-gradient-to-r from-red-600 to-orange-600 text-white font-bold py-3 rounded-lg">
                    DEPLOY RESCUE TEAM TO THIS LOCATION
                  </button>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Stats Footer */}
      <div className="mt-6 grid grid-cols-4 gap-4 text-center pt-4 border-t border-gray-700/50">
        <div>
          <div className="text-2xl font-bold text-green-500">847</div>
          <div className="text-xs text-gray-400">Total Cameras</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-cyan-500">623</div>
          <div className="text-xs text-gray-400">Online</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-yellow-500">189</div>
          <div className="text-xs text-gray-400">With Detections</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-purple-500">94%</div>
          <div className="text-xs text-gray-400">AI Accuracy</div>
        </div>
      </div>
    </div>
  );
};

export default PublicCameraFeed;